import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import * as yup from "yup";
import {
  TextField, Button, Typography, Box, Link, Alert,
  IconButton, InputAdornment, CircularProgress,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import StorefrontIcon from "@mui/icons-material/Storefront";
import { useNavigate, useLocation } from "react-router-dom";
import { useContext, useState } from "react";
import { ClienteFormContext } from "../../../context/cliente/ClienteContext";

const validation = yup.object({
  cli_mail: yup
    .string()
    .email("Debe ser un email válido")
    .required("El email es obligatorio"),
  cli_pass: yup
    .string()
    .min(6, "La contraseña debe tener al menos 6 caracteres")
    .required("La contraseña es obligatoria"),
  cli_pass_confirm: yup
    .string()
    .oneOf([yup.ref("cli_pass")], "Las contraseñas no coinciden")
    .required("Confirmá tu contraseña"),
});

export const Form = () => {
  const { resetAccount } = useContext(ClienteFormContext);
  const navigate = useNavigate();
  const location = useLocation();
  const name = location.state?.name || '';
  const cli_user = location.state?.cli_user || '';
  const mensaje = location.state?.mensaje || '';

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [showPassConfirm, setShowPassConfirm] = useState(false);
  const [successMessage, setSuccessMessage] = useState(false);
  const { register, handleSubmit, formState: { errors, isValid } } = useForm({
    mode: "onChange",
    resolver: yupResolver(validation),
  });

  const onSubmit = async (data) => {
    setLoading(true);
    setErrorMessage('');
    try {
      const res = await resetAccount({
        cli_user,
        cli_mail: data.cli_mail,
        cli_pass: data.cli_pass,
      });

      if (res?.status === 200 || res?.data?.mensaje) {
        setSuccessMessage(true);
      }
    } catch (err) {
      const codigo = err.response?.data?.codigo;
      if (codigo === 'NO_ENCONTRADO') {
        setErrorMessage('No encontramos tu cuenta. Por favor contactate con la empresa.');
      } else {
        setErrorMessage(err.response?.data?.message || err.response?.data?.error || 'Error al migrar la cuenta. Verificá los datos ingresados.');
      }
    } finally {
      setLoading(false);
    }
  };

  const inSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 'var(--radius-md)',
      fontFamily: 'var(--font-body)',
      backgroundColor: 'var(--bg-subtle)',
      '& fieldset': { border: '2px solid transparent' },
      '&:hover fieldset': { border: '2px solid var(--border-hover)' },
      '&.Mui-focused fieldset': { border: '2px solid var(--accent)' },
      '&.Mui-focused': { backgroundColor: 'var(--bg-card)' },
    },
    '& .MuiInputLabel-root': { fontFamily: 'var(--font-body)', fontSize: '0.9rem' },
  };

  return (
    <Box sx={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2, py: 4, background: 'linear-gradient(135deg, var(--bg-main) 0%, var(--bg-subtle) 100%)' }}>
      <Box sx={{ width: '100%', maxWidth: 440, display: 'flex', flexDirection: 'column', alignItems: 'center', backgroundColor: 'var(--bg-card)', p: { xs: 3, sm: 5 }, borderRadius: 'var(--radius-xl)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-lg)', animation: 'fadeInUp 0.5s ease both' }}>

        <Box sx={{ width: 56, height: 56, borderRadius: 'var(--radius-lg)', background: 'linear-gradient(135deg, var(--accent) 0%, #c0392b 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2, boxShadow: '0 4px 16px rgba(233,69,96,0.3)' }}>
          <StorefrontIcon sx={{ color: '#fff', fontSize: 28 }} />
        </Box>

        {successMessage ? (
          // ── Pantalla de éxito ──────────────────────────────────────────────
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', py: 2 }}>
            <Box sx={{ width: 64, height: 64, borderRadius: '50%', backgroundColor: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
              <CheckCircleIcon sx={{ fontSize: 36, color: '#16a34a' }} />
            </Box>
            <Typography sx={{ fontFamily: 'var(--font-heading)', fontWeight: 800, fontSize: '1.2rem', color: 'var(--text-primary)', mb: 1 }}>
              ¡Casi listo!
            </Typography>
            <Typography sx={{ fontFamily: 'var(--font-body)', fontSize: '0.9rem', color: 'var(--text-muted)', mb: 3 }}>
              Te enviamos un email a <strong>{register("cli_mail")?.value || 'tu correo'}</strong>. Revisá tu casilla y hacé clic en el enlace para activar tu cuenta.
            </Typography>
            <Alert severity="info" sx={{ width: '100%', borderRadius: 'var(--radius-md)', fontFamily: 'var(--font-body)', mb: 3 }}>
              Si no lo encontrás, revisá la carpeta de spam o correo no deseado.
            </Alert>
            <Button
              fullWidth
              variant="contained"
              onClick={() => navigate('/login')}
              sx={{ py: 1.5, borderRadius: 'var(--radius-md)', fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '0.95rem', textTransform: 'none', backgroundColor: 'var(--accent)', boxShadow: '0 4px 12px rgba(233,69,96,0.3)', '&:hover': { backgroundColor: 'var(--accent-hover)' } }}
            >
              Ir al inicio de sesión
            </Button>
          </Box>
        ) : (
          // ── Formulario ────────────────────────────────────────────────────
          <>
            <Typography sx={{ fontFamily: 'var(--font-heading)', fontWeight: 800, fontSize: '1rem', color: 'var(--text-primary)', mb: 0.5 }}>
              Bienvenido
            </Typography>
            <Typography sx={{ fontFamily: 'var(--font-heading)', fontWeight: 800, fontSize: '1rem', color: 'var(--text-primary)', mb: 0.5 }}>
              {name}
            </Typography>
            <Typography sx={{ fontFamily: 'var(--font-body)', fontSize: '0.85rem', color: 'var(--text-muted)', mb: 2, textAlign: 'center' }}>
              {mensaje || 'Verificá tu identidad y creá tus nuevas credenciales de acceso.'}
            </Typography>

            {errorMessage && (
              <Alert severity="error" sx={{ width: '100%', mb: 2, borderRadius: 'var(--radius-md)', fontFamily: 'var(--font-body)' }}>
                {errorMessage}
              </Alert>
            )}

            <Box component="form" noValidate sx={{ width: '100%' }} onSubmit={handleSubmit(onSubmit)}>
              <TextField
                margin="normal" fullWidth label="Nuevo correo electrónico" placeholder="tu@correo.com"
                {...register("cli_mail")} error={!!errors.cli_mail} helperText={errors.cli_mail?.message} sx={inSx}
              />
              <TextField
                margin="normal" fullWidth label="Nueva contraseña"
                type={showPass ? 'text' : 'password'} placeholder="••••••••"
                {...register("cli_pass")} error={!!errors.cli_pass} helperText={errors.cli_pass?.message} sx={inSx}
                InputProps={{ endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowPass(!showPass)} edge="end" size="small">{showPass ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}</IconButton></InputAdornment> }}
              />
              <TextField
                margin="normal" fullWidth label="Confirmá tu contraseña"
                type={showPassConfirm ? 'text' : 'password'} placeholder="••••••••"
                {...register("cli_pass_confirm")} error={!!errors.cli_pass_confirm} helperText={errors.cli_pass_confirm?.message} sx={inSx}
                InputProps={{ endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowPassConfirm(!showPassConfirm)} edge="end" size="small">{showPassConfirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}</IconButton></InputAdornment> }}
              />
              <Button
                type="submit" fullWidth variant="contained" disabled={!isValid || loading}
                sx={{ mt: 3, mb: 2, py: 1.5, borderRadius: 'var(--radius-md)', fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '0.95rem', textTransform: 'none', backgroundColor: 'var(--accent)', boxShadow: '0 4px 12px rgba(233,69,96,0.3)', '&:hover': { backgroundColor: 'var(--accent-hover)', transform: 'translateY(-1px)' }, '&:active': { transform: 'scale(0.98)' } }}
              >
                {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Actualizar cuenta'}
              </Button>
            </Box>

            <Typography sx={{ fontSize: '0.85rem', fontFamily: 'var(--font-body)', color: 'var(--text-secondary)', mt: 1 }}>
              ¿Recordaste tu contraseña?{' '}
              <Link href="/login" sx={{ fontWeight: 700, color: 'var(--accent)', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                Iniciá sesión
              </Link>
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
};

export default Form;